/**
 * Component: MarkdownRenderer
 * SpecReferenceID: GALEN-CHAT-V1
 * Requirements: GALEN-CHAT-V1-R02, GALEN-CHAT-V1-R03
 *
 * Renders markdown content from LLM responses with rich formatting:
 * headings, tables, code blocks, lists, blockquotes, and links.
 * Supports inline citation references that link to the citation block.
 *
 * GALEN-CHAT-V1-R02: Format LLM responses for clinical reading.
 * GALEN-CHAT-V1-R03: Display inline citation references in response text.
 */

import React, { useState, useCallback, useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MarkdownRendererProps {
  content: string;
  /** Number of citations available — enables inline ref styling for [doc1]..[docN]. */
  citationCount?: number;
  /** Callback when an inline citation badge is clicked (1-based index). */
  onCitationClick?: (citationNumber: number) => void;
}

/** Safely extract text content from React children for clipboard copy. */
function extractTextContent(children: React.ReactNode): string {
  if (typeof children === 'string') return children.replace(/\n$/, '');
  if (typeof children === 'number') return String(children);
  if (Array.isArray(children)) return children.map(extractTextContent).join('');
  return '';
}

/**
 * CodeBlock with copy-to-clipboard button.
 */
function CodeBlock({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.ReactElement {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    const text = extractTextContent(children);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard not available
    }
  }, [children]);

  return (
    <div className="chat-code-block group relative">
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-7 w-7 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={() => void handleCopy()}
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5" />
        )}
      </Button>
      <pre className={className}>
        <code>{children}</code>
      </pre>
    </div>
  );
}

/**
 * GALEN-CHAT-V1-R03 (Bug #3873 Issue 1): Pre-process markdown content to
 * convert [docN] citation patterns into markdown inline links.
 * React-markdown v10 does not support a custom `text` component, so we
 * transform the source string before rendering.
 *
 * [docN] patterns where N is within the citation range become [N](#citation-N),
 * which react-markdown renders as <a> elements. The custom `a` component in
 * MarkdownRenderer styles these as clickable citation badges.
 */
function preprocessCitationRefs(content: string, citationCount: number): string {
  if (citationCount === 0) return content;
  return content.replace(/\[doc(\d+)\]/g, (match, numStr: string) => {
    const num = parseInt(numStr, 10);
    if (num < 1 || num > citationCount) return match;
    return `[${num}](#citation-${num})`;
  });
}

export function MarkdownRenderer({
  content,
  citationCount = 0,
  onCitationClick,
}: MarkdownRendererProps): React.ReactElement {
  /**
   * GALEN-CHAT-V1-R03 (Bug #3873 Issue 1): Pre-process content to convert
   * [docN] patterns to markdown links for citation badge rendering.
   */
  const processedContent = useMemo(
    () => preprocessCitationRefs(content, citationCount),
    [content, citationCount]
  );

  return (
    <div className="chat-markdown prose prose-sm max-w-none dark:prose-invert">
      <Markdown
        remarkPlugins={[remarkGfm]}
        children={processedContent}
        components={{
          // Code blocks vs inline code
          code({ children, className, ...rest }) {
            const isBlock = className?.startsWith('language-');
            if (isBlock) {
              return (
                <CodeBlock className={className}>{children}</CodeBlock>
              );
            }
            return (
              <code className={className} {...rest}>
                {children}
              </code>
            );
          },
          // Wrap fenced code blocks
          pre({ children }) {
            return <>{children}</>;
          },
          // GALEN-CHAT-V1-R03 (Bug #3873 Issue 1): Style citation ref links as badges
          a({ href, children }) {
            const citationMatch = href?.match(/^#citation-(\d+)$/);
            if (citationMatch) {
              const num = parseInt(citationMatch[1] ?? '', 10);
              return (
                <a
                  href={href}
                  className="inline-flex h-4 min-w-4 items-center justify-center rounded bg-primary/10 px-1 text-[10px] font-semibold text-primary no-underline hover:bg-primary/20"
                  aria-label={`Citation ${num}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (onCitationClick) {
                      onCitationClick(num);
                    } else {
                      document.getElementById(`citation-${num}`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest',
                      });
                    }
                  }}
                >
                  {num}
                </a>
              );
            }
            return <a href={href}>{children}</a>;
          },
        }}
      />
    </div>
  );
}

export default MarkdownRenderer;
